import { HttpErrorResponse, HttpHandlerFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService } from 'app/core/auth/auth.service';
import { AuthUtils } from 'app/core/auth/auth.utils';
import { ErrorService } from 'app/modules/error/service/error.service';
import { environment } from 'environments/environment';
import { BehaviorSubject, Observable, catchError, finalize, skipWhile, switchMap, take, throwError } from 'rxjs';

let isRefreshingToken: boolean = false;
let tokenSubject: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);

/**
 * Intercept
 *
 * @param req
 * @param next
 */
export const authInterceptor = (req: HttpRequest<unknown>, next: HttpHandlerFn): Observable<any> => {
    if (req.url.includes('/assets/') || req.url.endsWith('.json')) {
        return next(req);
    }

    const authService = inject(AuthService);
    const router = inject(Router);
    const errorHandler = inject(ErrorService);

    // Request
    // If the access token didn't expire, add the Authorization header.
    // We won't add the Authorization header if the access token expired.
    // This will force the server to return a "401 Unauthorized" response
    // for the protected API routes which our response interceptor will
    // catch and delete the access token from the local storage while logging
    // the user out from the app.

    let headers = req.headers
        .set('X-ApplicationThemeId', `${environment.applicationThemeId}`)
        // Note: You can leave this for now if your backend expects it, but it is technically a response header
        .set('Access-Control-Allow-Origin', `${environment.appUrl}`);

    // 1. Pure public routes that require absolutely NO token headers
    const isPublicRoute = req.url.includes('v2/auth/login');

    // 2. Routes that should bypass the 15-minute idle eviction security check
    const bypassIdleCheck = isPublicRoute || req.url.includes('/auth/revoke-token');

    // FIXED: Only evaluate the 15-minute rule if the user actually HAS an access token.
    // This allows local startup assets (like 'en.json') to load safely without getting blocked.
    // if (!bypassIdleCheck && authService.accessToken) {
    //     // If expired, block it!
    //     if (!authService.isTokenValid()) {
    //         authService.handleManualExpiration();
    //         return throwError(
    //             () =>
    //                 new HttpErrorResponse({
    //                     status: 401,
    //                     statusText: 'Token Expired (15-Minute Rule)',
    //                 })
    //         );
    //     }

    //     // THE MAGIC LINE: The token is valid and they are making an active API call!
    //     // Reset the 15-minute idle timer right now.
    //     authService.resetIdleTimer();
    // }

    if (!isPublicRoute && authService.accessToken) {
        if (!AuthUtils.isTokenExpired(authService.accessToken)) {
            headers = headers.set('Authorization', `Bearer ${authService.accessToken}`);
        }
    }

    const newReq = req.clone({ headers });

    return next(newReq).pipe(
        catchError((error) => {
            // NEW: If the revoke-token fails, ignore it! Do not trigger the error page.
            if (req.url.includes('/auth/revoke-token')) {
                return throwError(() => error);
            }

            if (error instanceof HttpErrorResponse && authService.accessToken) {
                switch (error.status) {
                    case 400:
                        return handle400Error(error, router, authService);
                    case 401:
                        return handle401403Error(error, router, errorHandler);
                    case 403:
                        return handle401403Error(error, router, errorHandler);
                    case 404:
                        return handle404Error(error, router, errorHandler);
                    case 429:
                        return handle429Error(error, router, errorHandler);
                    default:
                        return throwError(() => error);
                }
            }
            router.navigate(['sign-in']);
            return throwError(() => error);
        })
    );
};

function handle429Error(error: any, router: Router, errorHandler: ErrorService) {
    gotoPageError(
        router,
        errorHandler,
        'Request Exceeded',
        `To keep things running smoothly for everyone, we’ve temporarily limited your access. <br/>Please wait a moment and try again shortly.`
    );
    return throwError(() => error);
}

function handle400Error(error: HttpErrorResponse, router: Router, authService: AuthService): any {
    if (error && error.status === 400 && error.error === 'invalid_grant') {
        // If we get a 400 and the error message is 'invalid_grant', the token is no longer valid so logout.
        authService.signOut(false);
        navigateToLoginPage(error, router);
        return throwError('Redirected to login page');
    }
    return throwError(error);
}

function handle401Error(
    request: HttpRequest<any>,
    next: HttpHandlerFn,
    router: Router,
    authService: AuthService,
    errorHandler: ErrorService
) {
    if (!isRefreshingToken) {
        isRefreshingToken = true;

        // Reset here so that the following requests wait until the token
        // comes back from the refreshToken call.
        tokenSubject.next(null);

        // send request for refreshing the tokens
        return authService.doRefreshTokens().pipe(
            switchMap((hasToken: boolean) => {
                if (hasToken) {
                    tokenSubject.next('hasToken');
                    return next(addTokens(request, authService.accessToken));
                } else {
                    return throwError(() => new Error('Authentication token missing!'));
                }
            }),
            catchError((error) => {
                if (error.status === 403) {
                    handle401403Error(error, router, errorHandler);
                } else {
                    // If there is an exception calling 'refreshToken', bad news so logout.
                    authService.signOut(false);
                }

                navigateToLoginPage(error, router);

                return throwError(error);
            }),
            finalize(() => (isRefreshingToken = false))
        );
    } else {
        // the refresh token is in progress, we will wait until tokenSubject has a non-null value
        // - which means the new token is ready and we can rery the request again
        return tokenSubject.pipe(
            skipWhile((value) => !value), // skip null values
            take(1), // I need the value only once
            switchMap(() => next(addTokens(request, authService.accessToken)))
        );
    }
}

function handle401403Error(error: any, router: Router, errorHandler: ErrorService) {
    // router.navigate(['/error/403']);
    gotoPageError(router, errorHandler, 'Access Denied', "You don't have permission to view this page or perform this action.");
    return throwError(() => error);
}

function navigateToLoginPage(error: any, router: Router) {
    router.navigate(['/sign-in']);
}

function addTokens(request: HttpRequest<any>, token: string): HttpRequest<any> {
    return request.clone({
        setHeaders: {
            Authorization: 'Bearer ' + token,
            'Content-Type': 'application/json',
        },
    });
}

function handle404Error(error: any, router: Router, errorHandler: ErrorService) {
    // router.navigate(['/error/404']);
    gotoPageError(router, errorHandler, 'Page Not Found', "The page you are looking for doesn't exist or has been removed.");
    return throwError(() => error);
}

function gotoPageError(router: Router, errorHandler: ErrorService, title: string = '', customMessage?: string) {
    const pageError = {
        title: title,
        message: customMessage || 'Something went wrong while loading the page. Please try again.',
        path: '/sign-in',
        icon: 'mat_outline:arrow_back',
    };

    // Push the error object to your state handler
    errorHandler.setError(pageError);

    router.navigate(['/error'], { replaceUrl: true });
}
